import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Volume2, VolumeX, Music } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import ProductCard from "@/components/storefront/ProductCard";
import { useStore } from "@/context/StoreContext";
import { useSound } from "@/context/SoundContext";
import { useSEO } from "@/lib/seo";
import { toast } from "sonner";

const fade = { hidden: { opacity: 0, y: 40 }, show: { opacity: 1, y: 0, transition: { duration: 1.2, ease: [0.22, 1, 0.36, 1] } } };
const Reveal = ({ children, className }) => (
  <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-110px" }} className={className}>{children}</motion.div>
);

const Home = () => {
  const { settings } = useStore();
  const seo = settings?.seo || {};
  useSEO({ title: seo.title, description: seo.description, image: seo.og_image });
  const [sections, setSections] = useState([]);
  useEffect(() => { api.get("/homepage").then((r) => setSections((r.data.sections || []).filter((s) => s.visible).sort((a, b) => a.order - b.order))).catch(() => {}); }, []);
  return (
    <div>
      <NextDropBanner />
      {sections.map((s) => <Section key={s.id} section={s} />)}
      <UpcomingDrops />
    </div>
  );
};

const Section = ({ section }) => {
  const d = section.data || {};
  switch (section.type) {
    case "hero": return <Hero d={d} />;
    case "drop_story": return <DropStory d={d} />;
    case "brand_statement": return <BrandStatement d={d} />;
    case "ori": return <OriSection d={d} />;
    case "destinations": return <Destinations d={d} />;
    case "culture": return <Culture d={d} />;
    case "complete_look": return <CompleteLook d={d} />;
    case "lifestyle": return <Lifestyle d={d} />;
    case "founder": return <Founder d={d} />;
    case "community": return <Community d={d} />;
    case "private_access": return <PrivateAccess d={d} />;
    default: return null;
  }
};

const Hero = ({ d }) => {
  // Sound is owned by the global player (SoundProvider), so it never doubles or
  // restarts on navigation. The hero video is always silent; the button below
  // simply controls that one persistent track.
  const { available, playing, toggle, track } = useSound();
  // The playing song's paired video leads the hero; otherwise fall back to the CMS hero video.
  const songVideo = track?.video_url ? mediaUrl(track.video_url) : null;
  const heroVideo = songVideo || (d.video ? mediaUrl(d.video) : null);

  return (
    <section className="relative h-[92vh] min-h-[560px] -mt-16 md:-mt-20 flex items-end">
      {heroVideo ? (
        <>
          <motion.video key={heroVideo} autoPlay muted loop playsInline preload="auto"
            poster={mediaUrl(d.poster || d.image_desktop)} src={heroVideo}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2, ease: "easeInOut" }}
            data-testid="hero-video" className="hero-pulse absolute inset-0 w-full h-full object-cover bg-cocoa" />
          {available && (
            <button onClick={toggle} data-testid="hero-sound-toggle"
              aria-label={playing ? "Pause music" : "Play music"}
              className="absolute bottom-6 right-6 z-20 flex items-center gap-2 border border-cream/30 rounded-full px-4 py-2 text-[0.6rem] tracking-[0.2em] uppercase text-cream/85 hover:border-gold hover:text-gold transition-colors backdrop-blur-sm bg-espresso/30">
              {playing ? <Volume2 size={14} /> : <VolumeX size={14} />}
              {playing ? "Sound On" : "Sound Off"}
            </button>
          )}
          {playing && (d.music_credit || track?.title) && (
            <div data-testid="hero-music-credit"
              className="absolute bottom-7 left-6 z-20 flex items-center gap-1.5 text-[0.58rem] tracking-[0.2em] uppercase text-cream/60">
              <Music size={11} /> {d.music_credit || track?.title}
            </div>
          )}
        </>
      ) : (
        <picture className="absolute inset-0">
          <source media="(max-width:768px)" srcSet={mediaUrl(d.image_mobile || d.image_desktop)} />
          <img src={mediaUrl(d.image_desktop)} alt={d.heading} className="w-full h-full object-cover" />
        </picture>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-espresso/70 via-espresso/10 to-espresso/20" />
      <div className="relative max-w-[1600px] mx-auto w-full px-6 md:px-10 pb-16 md:pb-24 text-cream">
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="overline text-gold mb-4">{d.overline}</motion.p>
        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.15 }}
          className="font-serif text-[2.5rem] sm:text-6xl md:text-8xl lg:text-9xl leading-[0.9] tracking-tight max-w-4xl break-words">{d.heading}</motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.4 }} className="mt-5 text-base md:text-lg text-cream/80 max-w-md">{d.subheading}</motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.55 }} className="flex flex-wrap gap-4 mt-8">
          <Link to={d.cta_href} className="bg-cream text-espresso px-8 py-4 text-[0.72rem] tracking-[0.2em] uppercase hover:bg-gold hover:text-cream transition-colors">{d.cta_label}</Link>
          <Link to={d.cta2_href} className="border border-cream/60 px-8 py-4 text-[0.72rem] tracking-[0.2em] uppercase hover:bg-cream/10 transition-colors">{d.cta2_label}</Link>
        </motion.div>
      </div>
    </section>
  );
};

const DropStory = ({ d }) => (
  <section className="py-36 md:py-52 px-6 md:px-10 max-w-[1600px] mx-auto text-center">
    <Reveal>
      <p className="overline text-gold mb-5">{d.overline}</p>
      <h2 className="font-serif text-5xl md:text-7xl leading-none tracking-tight mb-8">{d.heading}</h2>
      <p className="max-w-2xl mx-auto text-espresso/70 leading-relaxed mb-12">{d.copy}</p>
      <div className="flex flex-wrap justify-center gap-6 md:gap-10 mb-12">
        {(d.colours || []).map((c) => (
          <div key={c.name} className="flex flex-col items-center gap-2">
            <span className="w-12 h-12 md:w-16 md:h-16 rounded-full border border-espresso/10" style={{ background: c.swatch }} />
            <span className="text-[0.66rem] tracking-[0.16em] uppercase text-espresso/60">{c.name}</span>
          </div>
        ))}
      </div>
      <Link to={d.cta_href} className="inline-block border border-espresso px-10 py-4 text-[0.72rem] tracking-[0.22em] uppercase hover:bg-espresso hover:text-cream transition-colors">{d.cta_label}</Link>
    </Reveal>
  </section>
);

const BrandStatement = ({ d }) => (
  <section className="grid md:grid-cols-2 min-h-[70vh]">
    <div className="img-zoom order-2 md:order-1"><img src={mediaUrl(d.image_desktop)} alt="" className="w-full h-full object-cover min-h-[50vh]" /></div>
    <div className="bg-cocoa text-cream flex items-center px-8 md:px-20 py-20 order-1 md:order-2">
      <Reveal>
        <h2 className="font-serif text-5xl md:text-6xl leading-[0.95] mb-8">{d.heading}</h2>
        <p className="text-cream/70 leading-relaxed max-w-md">{d.copy}</p>
      </Reveal>
    </div>
  </section>
);

const OriSection = ({ d }) => (
  <section className="grid md:grid-cols-2 min-h-[80vh]">
    <div className="bg-ivory flex items-center px-8 md:px-20 py-24">
      <Reveal>
        <p className="overline text-gold mb-4">{d.overline}</p>
        <h2 className="font-serif text-7xl md:text-8xl leading-none mb-4">{d.heading}</h2>
        <p className="font-serif text-2xl md:text-3xl text-espresso/80 italic mb-6">{d.subheading}</p>
        <p className="text-espresso/70 leading-relaxed max-w-md mb-8">{d.copy}</p>
        <div className="flex gap-4">
          <Link to={d.cta_href} className="border border-espresso px-8 py-3.5 text-[0.7rem] tracking-[0.2em] uppercase hover:bg-espresso hover:text-cream transition-colors">{d.cta_label}</Link>
          <Link to={d.cta2_href} className="px-4 py-3.5 text-[0.7rem] tracking-[0.2em] uppercase hover-underline">{d.cta2_label}</Link>
        </div>
      </Reveal>
    </div>
    <div className="img-zoom"><img src={mediaUrl(d.image_desktop)} alt="ORI" className="w-full h-full object-cover min-h-[50vh]" /></div>
  </section>
);

const Destinations = ({ d }) => (
  <section className="py-32 md:py-44 px-6 md:px-10 max-w-[1600px] mx-auto">
    <Reveal><h2 className="font-serif text-4xl md:text-5xl mb-12">{d.heading}</h2></Reveal>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {(d.cards || []).map((c) => (
        <Link key={c.href} to={c.href} className="group relative img-zoom aspect-[3/4] overflow-hidden">
          <img src={mediaUrl(c.image)} alt={c.label} loading="lazy" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-espresso/25 group-hover:bg-espresso/40 transition-colors" />
          <span className="absolute bottom-6 left-6 text-cream font-serif text-2xl md:text-3xl">{c.label}</span>
        </Link>
      ))}
    </div>
  </section>
);

const Culture = ({ d }) => (
  <section className="bg-sand py-32 md:py-44 px-6 md:px-10">
    <div className="max-w-[1600px] mx-auto">
      <Reveal className="text-center mb-16">
        <p className="overline text-gold mb-4">{d.overline}</p>
        <h2 className="font-serif text-4xl md:text-6xl leading-tight max-w-3xl mx-auto">{d.heading}</h2>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-6">
        {(d.cards || []).map((c, i) => (
          <Reveal key={c.label}>
            <div className="img-zoom aspect-square overflow-hidden mb-4"><img src={mediaUrl(c.image)} alt={c.label} loading="lazy" className="w-full h-full object-cover" /></div>
            <h3 className="font-serif text-2xl mb-2">{c.label}</h3>
            <p className="text-sm text-espresso/60 leading-relaxed">{c.copy}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CompleteLook = ({ d }) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    Promise.all([d.product_slug, d.accessory_slug].filter(Boolean).map((s) => api.get(`/products/${s}`).then((r) => r.data.product).catch(() => null)))
      .then((r) => setItems(r.filter(Boolean)));
  }, [d.product_slug, d.accessory_slug]);
  if (!items.length) return null;
  return (
    <section className="py-32 md:py-44 px-6 md:px-10 max-w-[1600px] mx-auto">
      <Reveal className="mb-12">
        <p className="overline text-gold mb-3">{d.overline}</p>
        <h2 className="font-serif text-4xl md:text-6xl">{d.heading}</h2>
      </Reveal>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        {items.map((p) => <ProductCard key={p.id} product={p} />)}
      </div>
    </section>
  );
};

const Lifestyle = ({ d }) => (
  <section className="py-32 md:py-44 px-6 md:px-10 max-w-[1600px] mx-auto">
    <Reveal className="mb-12 text-center">
      <p className="overline text-gold mb-4">{d.overline}</p>
      <h2 className="font-serif text-4xl md:text-6xl">{d.heading}</h2>
    </Reveal>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {(d.images || []).map((img, i) => (
        <div key={i} className={`img-zoom overflow-hidden ${i % 2 === 0 ? "aspect-[3/4]" : "aspect-[3/4] md:mt-12"}`}>
          <img src={mediaUrl(img)} alt="" loading="lazy" className="w-full h-full object-cover" />
        </div>
      ))}
    </div>
  </section>
);

const Founder = ({ d }) => (
  <section className="grid md:grid-cols-2 bg-cocoa text-cream">
    <div className="img-zoom"><img src={mediaUrl(d.image_desktop)} alt="Nior Amara" className="w-full h-full object-cover min-h-[60vh]" /></div>
    <div className="flex items-center px-8 md:px-20 py-24">
      <Reveal>
        <p className="overline text-gold mb-5">{d.overline}</p>
        <h2 className="font-serif text-4xl md:text-6xl leading-tight mb-8">"{d.heading}"</h2>
        <p className="text-cream/70 leading-relaxed max-w-md mb-6">{d.copy}</p>
        <p className="font-serif text-xl italic text-gold">{d.signature}</p>
      </Reveal>
    </div>
  </section>
);

const Community = ({ d }) => (
  <section className="py-24 md:py-36 px-6 md:px-10 max-w-[1600px] mx-auto text-center">
    <Reveal className="mb-12">
      <p className="overline text-gold mb-3">{d.overline}</p>
      <h2 className="font-serif text-4xl md:text-5xl">{d.heading}</h2>
    </Reveal>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {(d.images || []).map((img, i) => <div key={i} className="img-zoom aspect-square overflow-hidden"><img src={mediaUrl(img)} alt="" loading="lazy" className="w-full h-full object-cover" /></div>)}
    </div>
  </section>
);

const PrivateAccess = ({ d }) => {
  const [email, setEmail] = useState("");
  const submit = async (e) => { e.preventDefault(); if (!email) return; try { await api.post("/newsletter", { email, source: "early-access", consent: true }); toast.success("You're on the list."); setEmail(""); } catch { toast.error("Please try again."); } };
  return (
    <section className="bg-espresso text-cream py-24 md:py-32 px-6 text-center">
      <Reveal>
        <p className="overline text-gold mb-4">{d.overline}</p>
        <h2 className="font-serif text-5xl md:text-7xl leading-none mb-5">{d.heading}</h2>
        <p className="text-cream/60 max-w-md mx-auto mb-10">{d.copy}</p>
        <form onSubmit={submit} className="max-w-md mx-auto flex border-b border-cream/30 focus-within:border-gold">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email"
            className="bg-transparent flex-1 py-3 outline-none placeholder:text-cream/40 text-sm" />
          <button className="text-[0.7rem] tracking-[0.2em] uppercase">{d.cta_label}</button>
        </form>
      </Reveal>
    </section>
  );
};

const NextDropBanner = () => {
  const [drop, setDrop] = useState(null);
  const [left, setLeft] = useState(0);
  useEffect(() => { api.get("/next-drop").then((r) => { if (r.data && r.data.at) setDrop(r.data); }).catch(() => {}); }, []);
  useEffect(() => {
    if (!drop) return;
    const tick = () => setLeft(new Date(drop.at).getTime() - Date.now());
    tick(); const t = setInterval(tick, 1000); return () => clearInterval(t);
  }, [drop]);
  if (!drop || left <= 0) return null;
  const d = Math.floor(left / 86400000), h = Math.floor(left / 3600000) % 24, m = Math.floor(left / 60000) % 60, s = Math.floor(left / 1000) % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <Link to={`/collections/${drop.slug}`} data-testid="home-drop-countdown"
      className="block bg-cocoa text-cream text-center py-3 px-6 hover:bg-espresso transition-colors">
      <span className="overline text-gold mr-3">{drop.name} · {drop.label}</span>
      <span className="font-serif text-lg tabular-nums">{pad(d)}d : {pad(h)}h : {pad(m)}m : {pad(s)}s</span>
    </Link>
  );
};

const DropCountdown = ({ at }) => {
  const [left, setLeft] = useState(new Date(at).getTime() - Date.now());
  useEffect(() => { const t = setInterval(() => setLeft(new Date(at).getTime() - Date.now()), 1000); return () => clearInterval(t); }, [at]);
  if (left <= 0) return <span className="text-gold">Now available</span>;
  const d = Math.floor(left / 86400000), h = Math.floor(left / 3600000) % 24, m = Math.floor(left / 60000) % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return <span className="tabular-nums">{d}d {pad(h)}h {pad(m)}m</span>;
};

// Homepage anticipation strip — shows any Coming Soon pieces with live countdowns.
const UpcomingDrops = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => {
      const now = Date.now();
      setItems((r.data || []).filter((p) => p.status === "coming_soon" || (p.drop_at && new Date(p.drop_at).getTime() > now)));
    }).catch(() => {});
  }, []);
  if (!items.length) return null;
  return (
    <section className="py-24 md:py-36 px-6 md:px-10 max-w-[1600px] mx-auto border-b border-espresso/10" data-testid="home-upcoming-drops">
      <Reveal className="mb-12">
        <p className="overline text-gold mb-3">The next chapter</p>
        <h2 className="font-serif text-4xl md:text-6xl">Arriving soon</h2>
      </Reveal>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
        {items.slice(0, 4).map((p, i) => {
          const img = (p.colors?.[0]?.images || [p.colors?.[0]?.main_image]).filter(Boolean)[0];
          return (
            <Reveal key={p.slug} delay={i * 0.08}>
              <Link to={`/products/${p.slug}`} className="group block" data-testid={`upcoming-${p.slug}`}>
                <div className="relative aspect-[3/4] overflow-hidden bg-sand mb-4">
                  {img ? <img src={mediaUrl(img)} alt={p.name} className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
                    : <div className="w-full h-full flex items-center justify-center text-espresso/40 font-serif italic">Arriving soon</div>}
                  <span className="absolute top-3 left-3 bg-cocoa text-cream text-[0.56rem] tracking-[0.16em] uppercase px-2.5 py-1">Coming Soon</span>
                </div>
                <h3 className="font-serif text-lg md:text-xl leading-tight">{p.name}</h3>
                {p.drop_at && <p className="mt-1.5 text-[0.66rem] tracking-[0.14em] uppercase text-espresso/50"><DropCountdown at={p.drop_at} /></p>}
              </Link>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};

export default Home;
